import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, DashboardLayout } from '../components';
import { adminApi, type PingWebhookHistory, type PingWebhookReceipt } from '../services/api';

const states = ['pending', 'processing', 'retry', 'processed', 'ignored', 'review_required'];
export function AdminPingWebhooks() {
  const { t, i18n } = useTranslation();
  const [rows, setRows] = useState<PingWebhookReceipt[]>([]);
  const [state, setState] = useState('');
  const [type, setType] = useState('');
  const [reference, setReference] = useState('');
  const [contextId, setContextId] = useState('');
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [selected, setSelected] = useState<PingWebhookReceipt | null>(null);
  const [history, setHistory] = useState<PingWebhookHistory | null>(null);
  const [reason, setReason] = useState('');
  const generation = useRef(0);
  const inspection = useRef(0);
  const load = useCallback(async (next?: string) => {
    const request = ++generation.current;
    setLoading(true); setError(false);
    try {
      const result = await adminApi.getPingWebhooks({ state: state || undefined, type: type || undefined, reference: reference || undefined, contextId: contextId || undefined, cursor: next });
      if (request !== generation.current) return;
      setRows(current => next ? [...current, ...result.items] : result.items);
      setCursor(result.nextCursor);
    } catch { if (request === generation.current) setError(true); }
    finally { if (request === generation.current) setLoading(false); }
  }, [state, type, reference, contextId]);
  const invalidate = useCallback(() => { generation.current++; inspection.current++; }, []);
  useEffect(() => { void load(); return invalidate; }, [load, invalidate]);
  const date = (value: string) => new Date(value).toLocaleString(i18n.language);
  const stateLabel = (value: string) => t(`adminPingWebhooks.states.${states.includes(value) ? value : 'unknown'}`);
  const inspect = async (row: PingWebhookReceipt) => {
    const request = ++inspection.current;
    setSelected(row); setHistory(null); setReason(''); setError(false);
    try { const result = await adminApi.getPingWebhookHistory(row); if (request === inspection.current) setHistory(result); }
    catch { if (request === inspection.current) setError(true); }
  };
  const reconsider = async () => {
    if (!selected || !reason.trim() || loading) return;
    setLoading(true); setError(false);
    try {
      await adminApi.reconsiderPingWebhook(selected, reason.trim());
      setSelected(null); setHistory(null); setReason('');
      await load();
    } catch { setError(true); setLoading(false); }
  };
  return <DashboardLayout><div className="container-fluid py-3">
    <h1 className="h4 mb-3">{t('adminPingWebhooks.title')}</h1>
    <p>{t('adminPingWebhooks.description')}</p>
    <Card><div className="row g-3 mb-3 align-items-end">
      <div className="col-12 col-md-6 col-xl-3"><label className="form-label" htmlFor="ping-state">{t('adminPingWebhooks.status')}</label>
        <select id="ping-state" className="form-select form-select-lg" value={state} onChange={e => setState(e.target.value)}>
          <option value="">{t('adminPingWebhooks.all')}</option>{states.map(s => <option key={s} value={s}>{stateLabel(s)}</option>)}
        </select></div>
      {([['type', type, setType], ['reference', reference, setReference], ['context', contextId, setContextId]] as const).map(([key, value, setter]) =>
        <div className="col-12 col-md-6 col-xl-3" key={key}><label htmlFor={`ping-${key}`} className="form-label">{t(`adminPingWebhooks.${key}`)}</label>
          <input id={`ping-${key}`} className="form-control form-control-lg" value={value} onChange={e => setter(e.target.value)} /></div>)}
    </div>
    <button type="button" className="btn btn-outline-primary btn-lg mb-3" disabled={loading} onClick={() => void load()}>{t('adminPingWebhooks.refresh')}</button>
    {error && <p className="alert alert-danger" role="alert">{t('adminPingWebhooks.error')}</p>}
    <div aria-busy={loading} className="row row-cols-1 g-3">
      {rows.map(row => <div className="col" key={`${row.endpoint_id}/${row.event_id}`}><article className="border rounded p-3 text-break">
        <h2 className="h6">{row.event_type}</h2>
        <p>{t('adminPingWebhooks.versionValue', { version: row.event_version })}</p>
        <p>{stateLabel(row.state)} · {t('adminPingWebhooks.attemptsValue', { count: row.attempts })}</p>
        <dl className="row mb-2">
          {([['event', row.event_id], ['context', row.context_id], ['providerReference', row.reference], ['payment', row.payment_id], ['payout', row.payout_id], ['recipient', row.recipient_id], ['payout', row.merchant_payout_ids?.join(', ')], ['received', date(row.received_at)], ['nextAttempt', ['pending', 'retry'].includes(row.state) ? date(row.next_attempt_at) : null], ['errorCode', row.last_error]] as const)
            .filter(([, value]) => value).map(([key, value], index) => <div className="col-12 col-md-6" key={`${key}-${index}`}><dt>{t(`adminPingWebhooks.${key}`)}</dt><dd>{value}</dd></div>)}
        </dl>
        <button type="button" className="btn btn-outline-primary btn-lg" onClick={() => void inspect(row)}>{t('adminPingWebhooks.inspect')}</button>
      </article></div>)}
    </div>
    {!loading && rows.length === 0 && <p>{t('adminPingWebhooks.empty')}</p>}
    {cursor && <button type="button" className="btn btn-outline-primary btn-lg mt-3" disabled={loading} onClick={() => void load(cursor)}>{t('adminPingWebhooks.more')}</button>}
    {selected && <section className="border rounded p-3 mt-4 text-break" aria-label={t('adminPingWebhooks.history')}>
      <h2 className="h5">{t('adminPingWebhooks.history')}</h2><p>{selected.event_id}</p>
      {history?.history.map(item => <p key={item.id}>{date(item.recorded_at)} · {stateLabel(item.state)} · {t('adminPingWebhooks.attemptsValue', { count: item.attempts })}{item.error_code && <code className="d-block text-break">{item.error_code}</code>}</p>)}
      {history?.requests.map(item => <div key={item.id}><p>{t('adminPingWebhooks.requested', { at: date(item.requested_at), actor: item.requested_by })}</p><p>{item.reason}</p></div>)}
      {['retry', 'review_required', 'ignored'].includes(selected.state) && <>
        <p>{t('adminPingWebhooks.reconsiderHelp')}</p>
        <label className="form-label" htmlFor="ping-reason">{t('adminPingWebhooks.reason')}</label>
        <textarea id="ping-reason" className="form-control mb-3" maxLength={1000} value={reason} onChange={e => setReason(e.target.value)} />
        <button type="button" className="btn btn-primary btn-lg" disabled={loading || !reason.trim()} onClick={() => void reconsider()}>{t('adminPingWebhooks.reconsider')}</button>
      </>}
    </section>}
    </Card>
  </div></DashboardLayout>;
}
